<template>
    <div class="reviews-shortcode" v-if="!loading.tour || tour">
        <!-- Loading state -->
        <div v-if="loading.tour" class="flex justify-center items-center py-8">
            <progress-bar class="w-[50vw] lg:w-[25vw]" mode="indeterminate" style="height: 2px" />
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="text-center text-red-500 py-4">
            {{ error }}
        </div>

        <!-- Content -->
        <div v-else-if="tour && reviews.length > 0" class="flex flex-col gap-6">
            <!-- Tour header -->
            <div v-if="showTourInfo" class="flex items-center gap-3 justify-center mb-4">
                <div v-if="tourImage" class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img :src="tourImage" :alt="tour.name" class="w-full h-full object-cover" />
                </div>
                <div class="flex flex-col items-start">
                    <div class="font-semibold text-lg">{{ tour.name }}</div>
                    <Rating v-if="tour.average_rating" :rating="tour.average_rating"
                        :number-of-reviews="totalReviews" />
                </div>
            </div>

            <!-- Carousel Layout -->
            <div v-if="layout === 'carousel'" class="px-3 relative">
                <ScrollSlider>
                    <div v-for="review in reviews" :key="review.id"
                        class="min-w-1/2 max-w-1/2 md:min-w-1/3 md:max-w-1/3 lg:min-w-1/4 lg:max-w-1/4 px-2">
                        <ReviewItem :review="review" />
                    </div>
                </ScrollSlider>
            </div>

            <!-- List Layout -->
            <div v-else class="flex flex-col gap-4">
                <div class="flex flex-col gap-4 items-center">
                    <div class="w-full md:w-3/4 flex flex-col gap-4">
                        <ReviewItem v-for="review in reviews" :key="review.id" :review="review" />
                    </div>
                </div>

                <!-- Load More / Pagination -->
                <div v-if="pagination && canLoadMore" class="flex flex-col items-center gap-2">
                    <div class="text-sm text-gray-500">
                        Showing {{ reviews.length }} of {{ pagination.total }}
                    </div>
                    <PButton :label="`Load ${nextPageCount} more`" @click="loadMore" :loading="loading.reviews"
                        :disabled="loading.reviews" severity="secondary" outlined />
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="tour && reviews.length === 0 && !loading.reviews" class="text-center text-gray-500 py-4">
            No reviews available
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { Button as PButton, ProgressBar } from 'primevue';
import { useApiProxy } from '@/Hook/useProxy';
import { Pagination, Review, Tour } from '@/types';
import ReviewItem from './Item.vue';
import Rating from './Rating.vue';
import ScrollSlider from '../ScrollSlider.vue';

// Helper functions for type conversion from shortcode strings
const toBool = (val: any, defaultVal: boolean = true): boolean => {
    if (typeof val === 'boolean') return val;
    if (typeof val === 'string') {
        if (val === 'false' || val === '0' || val === '') return false;
        return true;
    }
    return defaultVal;
};

const toNumber = (val: any, defaultVal: number): number => {
    if (typeof val === 'number') return val;
    const num = parseInt(val, 10);
    return isNaN(num) ? defaultVal : num;
};

// Props - using string types for shortcode compatibility
const props = defineProps<{
    tour_id?: string | number;
    tour?: string;
    resmark_id?: string;
    layout?: 'carousel' | 'list';
    per_page?: string | number;
    paginate?: string | boolean;
    limit?: string | number;
    show_tour_info?: string | boolean;
    cache?: string | boolean;
    // Server-resolved payload, injected by ShortcodeResolver for the
    // `[reviews ...]` shortcode so tour + reviews render during SSR.
    ssrData?: { tour?: Tour; reviews?: Review[]; pagination?: Pagination };
}>();

// Computed props with type conversion
const layoutMode = computed(() => props.layout ?? 'carousel');
const perPage = computed(() => toNumber(props.per_page, 10));
const shouldPaginate = computed(() => {
    if (props.paginate !== undefined) return toBool(props.paginate);
    return layoutMode.value === 'list';
});
const reviewLimit = computed(() => props.limit ? toNumber(props.limit, 10) : null);
const showTourInfo = computed(() => toBool(props.show_tour_info, true));
const useCache = computed(() => toBool(props.cache, true));

// State — seeded from the server payload so the shortcode renders during SSR.
const tour = ref<Tour | null>(props.ssrData?.tour ?? null);
const reviews = ref<Review[]>(props.ssrData?.reviews ?? []);
const pagination = ref<Pagination | null>(props.ssrData?.pagination ?? null);
const error = ref<string | null>(null);
const loading = ref({
    tour: false,
    reviews: false
});

// API hooks
const tourApi = useApiProxy<{ tour: Tour }>();
const reviewsApi = useApiProxy<{ reviews: Review[]; pagination?: Pagination }>();

// Computed
const tourImage = computed(() => {
    if (!tour.value?.images?.length) return null;
    const img = tour.value.images[tour.value.cover_photo] ?? tour.value.images[0];
    return img?.small ?? img?.medium;
});

const totalReviews = computed(() => pagination.value?.total ?? reviews.value.length);

const canLoadMore = computed(() => {
    if (!pagination.value) return false;
    return pagination.value.currentPage < pagination.value.totalPages;
});

const nextPageCount = computed(() => {
    if (!pagination.value) return 0;
    const remaining = pagination.value.total - reviews.value.length;
    return Math.min(remaining, pagination.value.perPage);
});

// Methods
const fetchTour = async () => {
    // If tour_id is provided directly, we may still need tour details
    const params = new URLSearchParams();

    if (props.tour_id) {
        params.append('tour_id', String(props.tour_id));
    } else if (props.tour) {
        params.append('slug', props.tour);
    } else if (props.resmark_id) {
        params.append('resmark_id', props.resmark_id);
    } else {
        error.value = 'Tour identifier required (tour_id, tour, or resmark_id)';
        return;
    }

    loading.value.tour = true;

    try {
        const url = route('get.tour.info') + '?' + params.toString();
        const result = await tourApi.request(url, {}, useCache.value, 600000);

        if (result?.tour) {
            tour.value = result.tour;
        } else {
            error.value = 'Tour not found';
        }
    } catch (e) {
        error.value = 'Failed to load tour';
    } finally {
        loading.value.tour = false;
    }
};

const fetchReviews = async (page: number = 1) => {
    if (!tour.value) return;

    loading.value.reviews = true;

    const params = new URLSearchParams();
    params.append('tour_id', String(tour.value.id));

    if (shouldPaginate.value) {
        params.append('paginate', 'true');
        params.append('per_page', String(perPage.value));
        params.append('page', String(page));
    } else if (reviewLimit.value) {
        params.append('paginate', 'false');
        params.append('limit', String(reviewLimit.value));
    }

    try {
        const url = route('get.reviews', { tour_id: tour.value.id }) + '?' + params.toString();
        const result = await reviewsApi.request(url, {}, useCache.value && page === 1, 300000);

        if (result?.reviews) {
            if (page === 1) {
                reviews.value = result.reviews;
            } else {
                reviews.value = [...reviews.value, ...result.reviews];
            }
            pagination.value = result.pagination ?? null;
        }
    } catch (e) {
        console.error('Failed to load reviews', e);
    } finally {
        loading.value.reviews = false;
    }
};

const loadMore = () => {
    if (!pagination.value || loading.value.reviews) return;
    fetchReviews(pagination.value.currentPage + 1);
};

// Lifecycle
onMounted(async () => {
    // Server already resolved tour + reviews for this shortcode — reuse them.
    if (props.ssrData?.tour) return;

    await fetchTour();
    if (tour.value) {
        await fetchReviews();
    }
});

// Watch for prop changes (in case shortcode re-renders)
watch(() => [props.tour_id, props.tour, props.resmark_id], async () => {
    reviews.value = [];
    pagination.value = null;
    await fetchTour();
    if (tour.value) {
        await fetchReviews();
    }
});
</script>
